import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Edit2, Loader2, Users, Inbox, MapPin, Database, Tag, FileText, Hash, GitBranch } from 'lucide-react';
import { newslettersApi, submissionsApi, NewsletterRecord, SubmissionRecord, extractLookupDisplay } from '../services/creatorApi';
import { toast } from 'sonner';
export function NewsletterDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [newsletter, setNewsletter] = useState<NewsletterRecord | null>(null);
  const [submissions, setSubmissions] = useState<SubmissionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [activeTab, setActiveTab] = useState<'submissions' | 'subscriptions'>('submissions');
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const newsletterData = await newslettersApi.get(id);
        setNewsletter(newsletterData);
        // Load submissions for this newsletter
        if (newsletterData?.Newsletter_ID) {
          setLoadingSubmissions(true);
          try {
            const subs = await submissionsApi.findByNewsletterId(newsletterData.Newsletter_ID);
            setSubmissions(subs);
          } catch {
            // Non-blocking
          } finally {
            setLoadingSubmissions(false);
          }
        }
      } catch (err) {
        console.error('Failed to load newsletter:', err);
        toast.error('Failed to load newsletter details');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-5281" data-mp="22heqyxwshow7">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-5282" data-mp="1oior5ail5b45" />
      </div>;
  }
  if (!newsletter) {
    return <div className="text-center py-12" data-id="element-5283" data-mp="1tfov9fvvk3sm">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-5284" data-mp="3v5e255dcyxyg">
          Newsletter not found
        </h3>
        <Link to="/newsletters" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-500" data-id="element-5285" data-mp="3qee3nvolc7tu">
          <ArrowLeft className="h-4 w-4 mr-2" data-id="element-5286" data-mp="3mdu5qpw7106n" />
          Back to Newsletters
        </Link>
      </div>;
  }
  // Get tag names from subform
  const tagNames = (newsletter.Newsletter_Tags || []).map(t => t.Tag_Name);
  // Get rules from subform
  const rules = newsletter.Newsletter_Category_Rules || [];
  // Split submissions by status
  const confirmedSubmissions = submissions.filter(s => s.Status === 'Confirmed');
  const pendingSubmissions = submissions.filter(s => s.Status !== 'Confirmed');
  return <div className="space-y-6" data-id="element-5287" data-mp="41fjfni0q9kp">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-5288" data-mp="2tpok80xbf75r">
        <div className="flex items-center gap-4" data-id="element-5289" data-mp="2opk1s5cuk2cw">
          <Link to="/newsletters" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-5290" data-mp="2oosjtxc6a353">
            <ArrowLeft className="h-5 w-5" data-id="element-5291" data-mp="2ejivnyk35oh1" />
          </Link>
          <div data-id="element-5292" data-mp="2jlrlwn5kqcfg">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5293" data-mp="2jl83l5c0hm0w">
              {newsletter.Newsletter}
            </h1>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500" data-id="element-5294" data-mp="2ohqb2cyq7ygx">
              <span className="flex items-center gap-1" data-id="element-5295" data-mp="2tbuewncp3qds">
                <MapPin className="h-3.5 w-3.5" data-id="element-5296" data-mp="2xcwgvbxzygjg" />
                {newsletter.Region || 'All Regions'}
              </span>
              <span className="flex items-center gap-1" data-id="element-5297" data-mp="329wmjlqxpha1">
                <Users className="h-3.5 w-3.5" data-id="element-5298" data-mp="37akec9mzm25b" />
                {newsletter.Team}
              </span>
              {newsletter.Newsletter_ID && <span className="flex items-center gap-1 font-mono text-xs text-gray-400" data-id="element-5299" data-mp="3c8vnsj9dean0">
                  <Hash className="h-3 w-3" data-id="element-5300" data-mp="1cxl0aky99g05" />
                  {newsletter.Newsletter_ID}
                </span>}
            </div>
          </div>
        </div>
        <Link to={`/newsletters/${id}/edit`} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-5301" data-mp="38bovl52jqld6">
          <Edit2 className="h-4 w-4" data-id="element-5302" data-mp="3hbv6qkarv996" />
          Edit Configuration
        </Link>
      </div>

      {/* Configuration Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-5303" data-mp="3cbyxo6n7wrmr">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-5304" data-mp="2opae7gik8r41">
          <h3 className="text-sm font-medium text-gray-500 mb-4" data-id="element-5305" data-mp="2knovhejchilx">Source</h3>
          <div className="flex items-start gap-3" data-id="element-5306" data-mp="2ye0nzejudjdl">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-5307" data-mp="2yfv8irr7b9h5">
              <FileText className="h-5 w-5 text-blue-600" data-id="element-5308" data-mp="2tn2301nwrx40" />
            </div>
            <div data-id="element-5309" data-mp="25xzqf884sugd">
              <div className="text-sm font-medium text-gray-900" data-id="element-5310" data-mp="25xhjnhnuexu7">
                {newsletter.Sign_Up_Form ? extractLookupDisplay(newsletter.Sign_Up_Form) : 'No form linked'}
              </div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-5311" data-mp="gg2j4b59s3r3">Sign Up Form</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-5312" data-mp="l8vngxwtta2n">
          <h3 className="text-sm font-medium text-gray-500 mb-4" data-id="element-5313" data-mp="7hqtocg53c08">
            CRM Destination
          </h3>
          <div className="space-y-4" data-id="element-5314" data-mp="cfilz7kp504y">
            <div className="flex items-start gap-3" data-id="element-5315" data-mp="1054jzrpc19bd">
              <div className="p-2 bg-purple-50 rounded-lg" data-id="element-5316" data-mp="105cwhihjmcm1">
                <Database className="h-5 w-5 text-purple-600" data-id="element-5317" data-mp="146ye1h50vf2k" />
              </div>
              <div data-id="element-5318" data-mp="q6xfw9g56lqs">
                {newsletter.Mailing_Lists && newsletter.Mailing_Lists.length > 0 ? <div className="space-y-1" data-id="element-5319" data-mp="v4yw8mm6o6yk">
                    {newsletter.Mailing_Lists.map((ml, i) => <div key={i} data-id="element-5320" data-mp="3aw4p4z8amgyw" data-mpi={i}>
                        <div className="text-sm font-medium text-gray-900" data-id="element-5321" data-mp="3ayz78k3u3pp5">
                          {ml.Mailing_List_Name}
                        </div>
                        {ml.Mailing_List_ID && <div className="text-xs text-gray-400 font-mono" data-id="element-5322" data-mp="2vvpfg2qpupna">
                            ID: {ml.Mailing_List_ID}
                          </div>}
                      </div>)}
                  </div> : <div className="text-sm font-medium text-gray-900" data-id="element-5323" data-mp="2i27ymppezr0y">
                    Not assigned
                  </div>}
                <div className="text-xs text-gray-500 mt-1" data-id="element-5324" data-mp="283shnfk3py56">
                  {newsletter.Mailing_Lists && newsletter.Mailing_Lists.length > 1 ? 'Mailing Lists' : 'Mailing List'}
                </div>
              </div>
            </div>
            {tagNames.length > 0 && <div className="flex items-start gap-3" data-id="element-5325" data-mp="2a0ry0uwmgf7">
                <div className="p-2 bg-green-50 rounded-lg" data-id="element-5326" data-mp="3thwmdjahsu2t">
                  <Tag className="h-5 w-5 text-green-600" data-id="element-5327" data-mp="3tierz6j1okne" />
                </div>
                <div data-id="element-5328" data-mp="4v8mjld0p1yl">
                  <div className="flex flex-wrap gap-1" data-id="element-5329" data-mp="4tnq1q7578y3">
                    {tagNames.map((name, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-5330" data-mp="9si9mucxp1j9" data-mpi={i}>
                        {name}
                      </span>)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-5331" data-mp="3n0lcygd32wyd">Applied Tags</div>
                </div>
              </div>}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-5332" data-mp="3w22zohkzxww1">
          <h3 className="text-sm font-medium text-gray-500 mb-4" data-id="element-5333" data-mp="3vzymta54dnjm">Automation</h3>
          <div className="flex items-center justify-between" data-id="element-5334" data-mp="179p99p472q2o">
            <div data-id="element-5335" data-mp="1bb2im96hxbec">
              <div className="text-2xl font-semibold text-gray-900" data-id="element-5336" data-mp="1baizip540gyq">
                {rules.length}
              </div>
              <div className="text-sm text-gray-500" data-id="element-5337" data-mp="1xdnlscnok4wf">Category Rules</div>
            </div>
            <div className="h-12 w-12 bg-orange-50 rounded-full flex items-center justify-center" data-id="element-5338" data-mp="1sfufzkqahcwo">
              <GitBranch className="h-6 w-6 text-orange-600" data-id="element-5339" data-mp="274qmhdcp6hv6" />
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-600" data-id="element-5340" data-mp="22bp6f20r8ca0">
            <span className="font-medium" data-id="element-5341" data-mp="2g3lt2qjnltz0">{submissions.length}</span> total
            submissions
            {confirmedSubmissions.length > 0 && <span className="text-green-600 ml-1" data-id="element-5342" data-mp="2b499qfrzr516">
                ({confirmedSubmissions.length} confirmed)
              </span>}
          </div>
        </div>
      </div>

      {/* Category Rules Summary */}
      {rules.length > 0 && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-5343" data-mp="vnigwoqbevpf">
          <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-5344" data-mp="qoxj0vyjco45">
            Category Rules
          </h3>
          <div className="space-y-2" data-id="element-5345" data-mp="32cj6hsrq75fh">
            {rules.map((rule, i) => <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-md" data-id="element-5346" data-mp="32en82425jscl" data-mpi={i}>
                <span className="text-gray-500" data-id="element-5347" data-mp="2slg0qdnuq29v">If</span>
                <span className="font-medium text-gray-900" data-id="element-5348" data-mp="2xlkkqdnyxf4u">
                  {rule.Form_Field}
                </span>
                <span className="text-gray-500" data-id="element-5349" data-mp="2jl9qr1rd3kfg">
                  {rule.Condition.replace(/_/g, ' ')}
                </span>
                {rule.Value && <span className="font-mono text-xs bg-gray-200 px-1.5 py-0.5 rounded" data-id="element-5350" data-mp="2nl1tgxu2qwcw">
                    {rule.Value}
                  </span>}
                <span className="text-gray-500" data-id="element-5351" data-mp="2enhslq04j22t">→</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-100" data-id="element-5352" data-mp="7wiausm8pfyh">
                  {rule.Category_Name}
                </span>
              </div>)}
          </div>
        </div>}

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-5353" data-mp="2zopvq8dp11rp">
        <div className="border-b border-gray-200" data-id="element-5354" data-mp="2lxte1clb24zf">
          <nav className="flex -mb-px" data-id="element-5355" data-mp="2lz4k5j283otp">
            <button onClick={() => setActiveTab('submissions')} className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'submissions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5356" data-mp="2qwnx8fn6pvk2">
              All Submissions ({submissions.length})
            </button>
            <button onClick={() => setActiveTab('subscriptions')} className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'subscriptions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5357" data-mp="39n5jc1dihswg">
              Confirmed ({confirmedSubmissions.length})
            </button>
          </nav>
        </div>

        <div className="p-6" data-id="element-5358" data-mp="1lq68l959tk1g">
          {loadingSubmissions ? <div className="text-center py-8" data-id="element-5359" data-mp="1zp5rs1a0l6q8">
              <Loader2 className="mx-auto h-6 w-6 text-blue-500 animate-spin" data-id="element-5360" data-mp="1znutggzc8r6i" />
              <p className="mt-2 text-sm text-gray-500" data-id="element-5361" data-mp="1upt97sq6eltg">
                Loading submissions...
              </p>
            </div> : activeTab === 'submissions' ? submissions.length === 0 ? <div className="text-center py-12" data-id="element-5362" data-mp="1306o6zkdkt4d">
                <Inbox className="mx-auto h-12 w-12 text-gray-300" data-id="element-5363" data-mp="1gq09j7wo8v9c" />
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-5364" data-mp="1gsd18cs8ejzl">
                  No submissions yet
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-5365" data-mp="o9pl7bs3dv5d">
                  Form submissions will appear here once the form is live and
                  customers start signing up.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-5366" data-mp="2jcchkovaefez">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-5367" data-mp="2oc8nwurgiixp">
                  <thead className="bg-gray-50" data-id="element-5368" data-mp="2oae2zgg6qqtm">
                    <tr data-id="element-5369" data-mp="2t2fbcra8fxed">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5370" data-mp="2t49tjxu4dber">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5371" data-mp="20ejffoshm9in">
                        Payload Preview
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5372" data-mp="25fgx809l51jm">
                        ID
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-5373" data-mp="2ebpw9dtnhy58">
                    {submissions.map(sub => {
                let payloadPreview = '';
                try {
                  const p = JSON.parse(sub.Form_Payload);
                  payloadPreview = p.email || p.Email || JSON.stringify(p).substring(0, 60) + '...';
                } catch {
                  payloadPreview = sub.Form_Payload?.substring(0, 60) || '-';
                }
                return <tr key={sub.ID} className="hover:bg-gray-50" data-id="element-5374" data-mp="vfwcoezf94hn">
                          <td className="px-4 py-3" data-id="element-5375" data-mp="qg9wpxs7la55">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sub.Status === 'Confirmed' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`} data-id="element-5376" data-mp="qfiedonmh8wt">
                              {sub.Status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 font-mono text-xs" data-id="element-5377" data-mp="2brjrx514k5o5">
                            {payloadPreview}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-400 font-mono" data-id="element-5378" data-mp="1dgik9403jt7f">
                            {sub.ID}
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div> : confirmedSubmissions.length === 0 ? <div className="text-center py-12" data-id="element-5379" data-mp="uixwy1czd2ox">
              <Users className="mx-auto h-12 w-12 text-gray-300" data-id="element-5380" data-mp="ujpngxrq0a8c" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-5381" data-mp="gs2r6xxs31pk">
                No confirmed subscriptions
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-5382" data-mp="ktxqjww5f50p">
                Subscribers who confirm their double opt-in will appear here.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-5383" data-mp="bresrr77zilb">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-5384" data-mp="buaor9zibx74">
                <thead className="bg-gray-50" data-id="element-5385" data-mp="3ockg1rmel9ff">
                  <tr data-id="element-5386" data-mp="3jd7lekpaiy2a">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5387" data-mp="3je0uvtfmw1o4">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5388" data-mp="22hn4ox6b1lr">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5389" data-mp="b47oonkrrgx9">
                      ID
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-5390" data-mp="kuahonrw4uvl">
                  {confirmedSubmissions.map(sub => {
                let email = '';
                try {
                  const p = JSON.parse(sub.Form_Payload);
                  email = p.email || p.Email || '-';
                } catch {
                  email = '-';
                }
                return <tr key={sub.ID} className="hover:bg-gray-50" data-id="element-5391" data-mp="wz6acpyg7kqz">
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-5392" data-mp="11vwr7fi2ukdg">
                          {email}
                        </td>
                        <td className="px-4 py-3" data-id="element-5393" data-mp="16uhqncpi900u">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-5394" data-mp="1bn15se3uc37r">
                            Confirmed
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 font-mono" data-id="element-5395" data-mp="1kpac0rs2tafc">
                          {sub.ID}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}